/**
 * ============================================================================
 *  Sunset Eyewear — Wishlist (client-side + shareable)
 * ============================================================================
 *  • Stores wishlist in localStorage (key: "sunset_wishlist")
 *  • Auto-injects a heart toggle into every <product-card> on the page
 *  • Updates header counter + bubble in real time
 *  • Renders a wishlist grid into [data-wishlist-grid] when present
 *  • Supports shareable URLs:  /pages/wishlist?list=handle1,handle2,...
 *    When `?list=` is present, the page imports those handles and offers a
 *    "Save to my wishlist" button.
 *  • Public API on window.SunsetWishlist
 * ============================================================================
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'sunset_wishlist';
  const EVENT_NAME  = 'wishlist:change';

  // Display settings (set from theme.liquid via window.SunsetWishlistConfig)
  const CONFIG = window.SunsetWishlistConfig || {};
  const DISPLAY = (CONFIG.display || 'icon').toLowerCase(); // 'icon' | 'label' | 'both'
  const ICON_TYPE = (CONFIG.icon || 'heart').toLowerCase();
  const USE_BACKGROUND = CONFIG.useBackground !== false;
  const LABEL_INACTIVE = CONFIG.labelInactive || 'Save';
  const LABEL_ACTIVE   = CONFIG.labelActive   || 'Saved';

  // ---- Storage helpers -----------------------------------------------------
  function read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter(Boolean) : [];
    } catch (_) {
      return [];
    }
  }
  function write(handles) {
    const unique = Array.from(new Set(handles.filter(Boolean)));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(unique));
    document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: unique }));
    return unique;
  }
  function has(handle)    { return read().indexOf(handle) !== -1; }
  function add(handle)    { const a = read(); if (a.indexOf(handle) === -1) a.push(handle); return write(a); }
  function remove(handle) { return write(read().filter((h) => h !== handle)); }
  function toggle(handle) { return has(handle) ? remove(handle) : add(handle); }
  function clear()        { return write([]); }
  function count()        { return read().length; }

  // ---- Toast feedback ------------------------------------------------------
  function toast(message) {
    const el = document.querySelector('x-toast');
    if (el && typeof el.show === 'function') {
      el.show(message);
    } else {
      const live = document.getElementById('status-announcement');
      if (live) live.textContent = message;
    }
  }

  // ---- SVG Icon Library ----------------------------------------------------
  const ICONS = {
    heart: {
      outline: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>',
      filled:  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true"><path d="M12 21.23l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.41L12 21.23z"/></svg>'
    },
    bookmark: {
      outline: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>',
      filled:  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>'
    },
    star: {
      outline: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>',
      filled:  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>'
    },
    plus: {
      outline: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>',
      filled:  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>'
    },
    check: {
      outline: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>',
      filled:  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm-1 14.41l-4-4 1.41-1.41L11 13.59l5.59-5.59L18 9.41z"/></svg>'
    },
    eye: {
      outline: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>',
      filled:  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true"><path d="M12 4C5 4 1 12 1 12s4 8 11 8 11-8 11-8-4-8-11-8zm0 13a5 5 0 1 1 5-5 5 5 0 0 1-5 5z"/><circle cx="12" cy="12" r="2.5"/></svg>'
    },
    flag: {
      outline: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>',
      filled:  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1zM4 22V15h-2v7z"/></svg>'
    },
    pin: {
      outline: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>',
      filled:  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true"><path d="M12 2a8 8 0 0 0-8 8c0 7 8 13 8 13s8-6 8-13a8 8 0 0 0-8-8zm0 11a3 3 0 1 1 3-3 3 3 0 0 1-3 3z"/></svg>'
    }
  };

  // X (close/remove) icon — used on wishlist page for "remove" action
  const ICON_CLOSE = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

  // Detect if we're on the wishlist page (must be a function, not a const,
  // because the script may load before the DOM is fully parsed)
  function isWishlistPage() {
    return !!document.querySelector('[data-wishlist-grid]');
  }

  function getIcon(active) {
    const set = ICONS[ICON_TYPE] || ICONS.heart;
    return active ? set.filled : set.outline;
  }

  // Build the inner HTML of the toggle based on display mode + state
  function buildButtonContent(active) {
    const icon  = getIcon(active);
    const label = active ? LABEL_ACTIVE : LABEL_INACTIVE;
    if (DISPLAY === 'label') return `<span>${label}</span>`;
    if (DISPLAY === 'both')  return `${icon}<span>${label}</span>`;
    return icon;
  }

  function variantClass() {
    const base = (DISPLAY === 'label') ? 'wishlist-toggle--label'
              : (DISPLAY === 'both')  ? 'wishlist-toggle--both'
              : 'wishlist-toggle--icon';
    return base + (USE_BACKGROUND ? '' : ' wishlist-toggle--no-bg');
  }

  // ---- Inject heart button onto every <product-card> -----------------------
  function injectButtons(root) {
    const cards = (root || document).querySelectorAll('product-card[handle]:not([data-wishlist-bound])');
    cards.forEach((card) => {
      const handle = card.getAttribute('handle');
      if (!handle) return;
      card.setAttribute('data-wishlist-bound', 'true');

      // Find the figure (image area) — fallback to the card itself.
      const fig = card.querySelector('.product-card__figure') || card;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wishlist-toggle ' + variantClass();
      btn.setAttribute('data-wishlist-toggle', handle);
      const isIn = has(handle);
      btn.setAttribute('aria-label', isIn ? LABEL_ACTIVE : LABEL_INACTIVE);
      btn.setAttribute('aria-pressed', isIn ? 'true' : 'false');
      btn.innerHTML = buildButtonContent(isIn);

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        const wasIn = has(handle);
        toggle(handle);
        const nowActive = !wasIn;
        btn.setAttribute('aria-pressed', nowActive ? 'true' : 'false');
        btn.setAttribute('aria-label', nowActive ? LABEL_ACTIVE : LABEL_INACTIVE);
        btn.innerHTML = buildButtonContent(nowActive);
        btn.classList.add('wishlist-toggle--bump');
        setTimeout(() => btn.classList.remove('wishlist-toggle--bump'), 300);
        toast(nowActive ? (LABEL_ACTIVE + '!') : 'Removed');

        // On wishlist page, animate card out then re-render grid
        if (isWishlistPage() && !nowActive) {
          const card = btn.closest('product-card');
          if (card) {
            card.classList.add('wishlist-removing');
            setTimeout(() => {
              document.querySelectorAll('[data-wishlist-grid]').forEach(renderGrid);
            }, 380);
          } else {
            document.querySelectorAll('[data-wishlist-grid]').forEach(renderGrid);
          }
        }
      });

      // Style position absolute, top-right of figure
      if (getComputedStyle(fig).position === 'static') {
        fig.style.position = 'relative';
      }
      fig.appendChild(btn);
    });
  }

  // Keep buttons in sync if state changes (e.g. removed from another tab/page)
  function syncButtons() {
    const handles = read();
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const h = btn.getAttribute('data-wishlist-toggle');
      const isIn = handles.indexOf(h) !== -1;
      btn.setAttribute('aria-pressed', isIn ? 'true' : 'false');
      btn.setAttribute('aria-label', isIn ? LABEL_ACTIVE : LABEL_INACTIVE);
      btn.innerHTML = buildButtonContent(isIn);
    });
  }

  // ---- Header counter ------------------------------------------------------
  function syncCounters() {
    const c = count();
    // Number bubbles (if any merchant inserts a numeric badge)
    document.querySelectorAll('[data-wishlist-count]:not([data-wishlist-dot])').forEach((el) => {
      el.textContent = c;
      el.toggleAttribute('hidden', c === 0);
    });
    // Plain dots (matches cart-count style: no number, just visible/hidden)
    document.querySelectorAll('[data-wishlist-dot]').forEach((el) => {
      const wasHidden = el.hasAttribute('hidden');
      el.toggleAttribute('hidden', c === 0);
      // Pop animation when dot appears
      if (wasHidden && c > 0) {
        el.classList.remove('dot-pop');
        void el.offsetWidth; // force reflow to restart animation
        el.classList.add('dot-pop');
      }
    });
    document.querySelectorAll('[data-wishlist-empty-state]').forEach((el) => {
      el.style.display = c === 0 ? '' : 'none';
    });
  }

  // ---- Wishlist page renderer ---------------------------------------------
  async function renderGrid(grid) {
    const handles = currentHandles();
    grid.innerHTML = '';

    const emptyEl = document.querySelector('[data-wishlist-empty]');

    if (!handles.length) {
      if (emptyEl) emptyEl.style.display = '';
      return;
    }
    if (emptyEl) emptyEl.style.display = 'none';

    for (const handle of handles) {
      // Fetch real product card HTML from Shopify section rendering API
      try {
        const res = await fetch('/products/' + encodeURIComponent(handle) + '?section_id=wishlist-card');
        if (!res.ok) continue;
        const html = await res.text();
        const wrapper = document.createElement('div');
        wrapper.innerHTML = html;
        // Extract the product-card element from the response
        const card = wrapper.querySelector('product-card') || wrapper.firstElementChild;
        if (card) grid.appendChild(card);
      } catch (_) { /* ignore */ }
    }
    // Re-inject wishlist buttons on freshly rendered <product-card> nodes
    injectButtons(grid);
  }

  // ---- Shareable URL helpers ----------------------------------------------
  function urlHandles() {
    try {
      const params = new URLSearchParams(window.location.search);
      const list = params.get('list');
      if (!list) return null;
      return list.split(',').map((s) => s.trim()).filter(Boolean);
    } catch (_) { return null; }
  }

  function currentHandles() {
    return urlHandles() || read();
  }

  function buildShareUrl() {
    const handles = read();
    const url = new URL(window.location.href);
    url.search = ''; // reset query
    if (handles.length) url.searchParams.set('list', handles.join(','));
    return url.toString();
  }

  async function share() {
    const url = buildShareUrl();
    const title = document.title || 'My wishlist';
    if (navigator.share) {
      try { await navigator.share({ title, url }); return; } catch (_) {}
    }
    try {
      await navigator.clipboard.writeText(url);
      toast('Wishlist link copied');
    } catch (_) {
      // Fallback: prompt
      window.prompt('Copy this link', url);
    }
  }

  function importFromUrl() {
    const handles = urlHandles();
    if (!handles || !handles.length) return;
    write(handles);
    toast('Wishlist imported');
    // Strip the ?list= query so refresh doesn't re-import
    const clean = new URL(window.location.href);
    clean.searchParams.delete('list');
    history.replaceState({}, '', clean.toString());
    syncCounters();
    syncButtons();
    document.querySelectorAll('[data-wishlist-grid]').forEach(renderGrid);
  }

  // ---- Custom confirm modal ------------------------------------------------
  function showConfirmModal(title, message, onConfirm) {
    // Remove any existing modal
    const existing = document.querySelector('.wishlist-modal-overlay');
    if (existing) existing.remove();

    const overlay = document.createElement('div');
    overlay.className = 'wishlist-modal-overlay';
    overlay.innerHTML = `
      <div class="wishlist-modal">
        <div class="wishlist-modal__title">${title}</div>
        <div class="wishlist-modal__text">${message}</div>
        <div class="wishlist-modal__actions">
          <button type="button" class="wishlist-modal__btn" data-modal-cancel>Cancel</button>
          <button type="button" class="wishlist-modal__btn wishlist-modal__btn--confirm" data-modal-confirm>Clear All</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    // Trigger open animation
    requestAnimationFrame(() => overlay.classList.add('is-open'));

    function closeModal() {
      overlay.classList.remove('is-open');
      setTimeout(() => overlay.remove(), 300);
    }

    overlay.querySelector('[data-modal-cancel]').addEventListener('click', closeModal);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) closeModal(); });
    overlay.querySelector('[data-modal-confirm]').addEventListener('click', () => {
      closeModal();
      onConfirm();
    });
  }

  // ---- Bind global controls (share button, clear button, etc.) ------------
  function bindControls() {
    document.querySelectorAll('[data-wishlist-share]').forEach((btn) => {
      if (btn.dataset.bound) return; btn.dataset.bound = '1';
      btn.addEventListener('click', share);
    });
    document.querySelectorAll('[data-wishlist-clear]').forEach((btn) => {
      if (btn.dataset.bound) return; btn.dataset.bound = '1';
      btn.addEventListener('click', () => {
        if (!count()) return;
        showConfirmModal(
          'Clear Wishlist',
          'Are you sure you want to remove all items from your wishlist? This action cannot be undone.',
          () => {
            clear();
            document.querySelectorAll('[data-wishlist-grid]').forEach(renderGrid);
          }
        );
      });
    });
    document.querySelectorAll('[data-wishlist-import]').forEach((btn) => {
        if (btn.dataset.bound) return; btn.dataset.bound = '1';
      btn.addEventListener('click', importFromUrl);
    });
  }

  // ---- PDP wishlist button -------------------------------------------------
  function pdpIcon(active) {
    const set = ICONS[ICON_TYPE] || ICONS.heart;
    return active ? set.filled : set.outline;
  }

  function injectPdpButtons() {
    document.querySelectorAll('[data-wishlist-pdp-handle]:not([data-wishlist-pdp-bound])').forEach((btn) => {
      const handle = btn.getAttribute('data-wishlist-pdp-handle');
      if (!handle) return;
      btn.setAttribute('data-wishlist-pdp-bound', '1');

      const iconEl = btn.querySelector('[data-wishlist-pdp-icon]');
      const isIn = has(handle);
      if (iconEl) iconEl.innerHTML = pdpIcon(isIn);
      btn.classList.toggle('is-active', isIn);
      btn.setAttribute('aria-pressed', isIn ? 'true' : 'false');

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const wasIn = has(handle);
        toggle(handle);
        const nowActive = !wasIn;
        if (iconEl) iconEl.innerHTML = pdpIcon(nowActive);
        btn.classList.toggle('is-active', nowActive);
        btn.setAttribute('aria-pressed', nowActive ? 'true' : 'false');
        toast(nowActive ? (LABEL_ACTIVE + '!') : 'Removed');
      });
    });
  }

  function syncPdpButtons() {
    document.querySelectorAll('[data-wishlist-pdp-handle][data-wishlist-pdp-bound]').forEach((btn) => {
      const handle = btn.getAttribute('data-wishlist-pdp-handle');
      const isIn = has(handle);
      const iconEl = btn.querySelector('[data-wishlist-pdp-icon]');
      if (iconEl) iconEl.innerHTML = pdpIcon(isIn);
      btn.classList.toggle('is-active', isIn);
      btn.setAttribute('aria-pressed', isIn ? 'true' : 'false');
    });
  }

  // ---- Init ---------------------------------------------------------------
  function init() {
    injectButtons(document);
    injectPdpButtons();
    bindControls();
    syncCounters();
    document.querySelectorAll('[data-wishlist-grid]').forEach(renderGrid);

    // Listen for state changes (also fired across tabs via storage event)
    document.addEventListener(EVENT_NAME, () => {
      syncCounters();
      syncButtons();
      syncPdpButtons();
    });
    window.addEventListener('storage', (e) => {
      if (e.key !== STORAGE_KEY) return;
      syncCounters();
      syncButtons();
      syncPdpButtons();
      document.querySelectorAll('[data-wishlist-grid]').forEach(renderGrid);
    });

    // Re-scan on Shopify Theme Editor section reloads (so heart buttons appear
    // on cards rendered after init)
    document.addEventListener('shopify:section:load',  (e) => injectButtons(e.target));

    // Also observe DOM for dynamically added product cards (filtering, AJAX)
    if (window.MutationObserver) {
      new MutationObserver((muts) => {
        let needsScan = false;
        for (const m of muts) {
          for (const node of m.addedNodes) {
            if (node.nodeType === 1 && (node.matches?.('product-card') || node.querySelector?.('product-card'))) {
              needsScan = true; break;
            }
          }
          if (needsScan) break;
        }
        if (needsScan) injectButtons(document);
      }).observe(document.body, { childList: true, subtree: true });
    }

    // Wishlist page banner: shareable import prompt
    if (urlHandles()) {
      const banner = document.querySelector('[data-wishlist-import-banner]');
      if (banner) banner.style.display = '';
    }
  }

  // ---- Public API ---------------------------------------------------------
  window.SunsetWishlist = {
    add, remove, toggle, has, read, write, clear, count,
    share, importFromUrl, buildShareUrl,
    EVENT_NAME, STORAGE_KEY
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
